import { useState } from "react";
import { isIntakeAiUnavailableError } from "../../../clients/workIntakeClient";
import type { AssetId } from "../../../lib/workIds";
import {
  type IntakeNextStep,
  nextStepAfterIntake,
  recoverUnavailableSend,
} from "../../../lib/workRequestIntake";
import { useWorkRequestIntakeMutation } from "../../../mutations/useWorkRequestIntakeMutation";
import type {
  WorkRequestAssetCandidate,
  WorkRequestDraft,
  WorkRequestIntakeState,
} from "../../../types/workRequest";
import type { AiNotice } from "./AiNoticeAlert";

export type IntakeChatPhase = "describe" | "resolve_asset";

export type IntakeChatEntry =
  | { role: "assistant"; content: string }
  | { role: "user"; content: string }
  | { role: "asset"; assetName: string };

const INITIAL_PROMPT =
  "What's going on? Describe what you need and I'll route it to the right team.";
const REVIEWING_MESSAGE = "Reviewing request now.";

function initialIntakeState(
  initialAssetId: AssetId | null,
  initialAssetName: string | null,
): WorkRequestIntakeState {
  return {
    traceId: crypto.randomUUID(),
    assistantMessage: INITIAL_PROMPT,
    stage: "collect_request",
    draft: {
      assetId: initialAssetId,
      assetName: initialAssetName,
      assetKind: null,
      workRequestDescription: null,
      workRequestTitle: null,
      priorityCode: null,
      categoryIds: [],
    },
    assetCandidates: [],
    readyToCreate: false,
    aiAssistance: "AVAILABLE",
  };
}

function initialChatEntries(): IntakeChatEntry[] {
  return [{ role: "assistant", content: INITIAL_PROMPT }];
}

type UseNewWorkRequestIntakeOptions = {
  initialAssetId: AssetId | null;
  initialAssetName: string | null;
  onAssessment: (draft: WorkRequestDraft) => void;
  onClearNotice: () => void;
  onRouteToForm: (notice: AiNotice) => void;
};

export function useNewWorkRequestIntake({
  initialAssetId,
  initialAssetName,
  onAssessment,
  onClearNotice,
  onRouteToForm,
}: UseNewWorkRequestIntakeOptions) {
  const [message, setMessage] = useState("");
  const [intakeState, setIntakeState] = useState(() =>
    initialIntakeState(initialAssetId, initialAssetName),
  );
  const [chatPhase, setChatPhase] = useState<IntakeChatPhase>("describe");
  const [chatEntries, setChatEntries] = useState(initialChatEntries);
  const intakeMutation = useWorkRequestIntakeMutation();

  function reset() {
    setMessage("");
    setIntakeState(initialIntakeState(initialAssetId, initialAssetName));
    setChatPhase("describe");
    setChatEntries(initialChatEntries());
  }

  function appendEntries(...entries: IntakeChatEntry[]) {
    setChatEntries((current) => [...current, ...entries]);
  }

  function routeIfFormStep(step: IntakeNextStep) {
    if (step === "form-unavailable") {
      onRouteToForm("unavailable");
      return true;
    }
    if (step === "form-incomplete") {
      onRouteToForm("incomplete");
      return true;
    }
    return false;
  }

  function handleIntakeSuccess(nextState: WorkRequestIntakeState) {
    setIntakeState(nextState);
    const step = nextStepAfterIntake(nextState);
    if (routeIfFormStep(step)) return;
    onClearNotice();
    if (step === "pick-asset") {
      appendEntries({
        role: "assistant",
        content: "I need to pin down the machine. Which one is this?",
      });
      setChatPhase("resolve_asset");
      return;
    }
    if (step === "assess" && nextState.draft.assetName) {
      appendEntries(
        { role: "asset", assetName: nextState.draft.assetName },
        { role: "assistant", content: REVIEWING_MESSAGE },
      );
      onAssessment(nextState.draft);
      return;
    }
    appendEntries({ role: "assistant", content: nextState.assistantMessage });
    setChatPhase(
      nextState.stage === "resolve_asset" ? "resolve_asset" : "describe",
    );
  }

  function sendToBedrock(userMessage: string) {
    const entriesBeforeSend = chatEntries;
    appendEntries({ role: "user", content: userMessage });
    intakeMutation.mutate(
      { state: intakeState, userMessage },
      {
        onSuccess: handleIntakeSuccess,
        onError: (error) => {
          setChatEntries(entriesBeforeSend);
          if (isIntakeAiUnavailableError(error)) {
            if (
              recoverUnavailableSend(intakeState, userMessage).restoreMessage
            ) {
              setMessage(userMessage);
            } else {
              setIntakeState(
                (current) => recoverUnavailableSend(current, userMessage).state,
              );
            }
            onRouteToForm("unavailable");
            return;
          }
          setMessage(userMessage);
        },
      },
    );
  }

  function handleSend() {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || intakeMutation.isPending) return;
    setMessage("");
    sendToBedrock(trimmedMessage);
  }

  function handleCandidate(candidate: WorkRequestAssetCandidate) {
    if (intakeMutation.isPending) return;
    const entriesBeforeSend = chatEntries;
    appendEntries({ role: "asset", assetName: candidate.assetName });
    const selectedState: WorkRequestIntakeState = {
      ...intakeState,
      stage: "resolve_asset",
      draft: {
        ...intakeState.draft,
        assetId: candidate.assetId,
        assetName: candidate.assetName,
        assetKind: candidate.kind ?? null,
      },
      assetCandidates: [],
    };
    intakeMutation.mutate(
      { state: selectedState, userMessage: candidate.assetName },
      {
        onSuccess: (nextState) => {
          setIntakeState(nextState);
          const step = nextStepAfterIntake({
            ...nextState,
            assetCandidates: [],
            draft: {
              ...nextState.draft,
              assetName: nextState.draft.assetName ?? candidate.assetName,
            },
          });
          if (routeIfFormStep(step)) return;
          onClearNotice();
          if (step !== "assess") {
            onRouteToForm("incomplete");
            return;
          }
          appendEntries({ role: "assistant", content: REVIEWING_MESSAGE });
          onAssessment(nextState.draft);
        },
        onError: (error) => {
          setChatEntries(entriesBeforeSend);
          if (isIntakeAiUnavailableError(error)) {
            setIntakeState(selectedState);
            onRouteToForm("unavailable");
          }
        },
      },
    );
  }

  return {
    chatEntries,
    chatPhase,
    handleCandidate,
    handleSend,
    intakeState,
    isPending: intakeMutation.isPending,
    message,
    reset,
    setIntakeState,
    setMessage,
  };
}
